/**
 * @file 本だなカードの表紙 (縮小画像、無ければ先頭ページの画像) を引く hook。
 *
 * @what: 資料が表紙の縮小画像 (cover_path。CLI が先頭ページを幅300pxに縮めて上げたもの) を持てば
 *   それを pageImageCache 経由で object URL にする。持たない資料は pages 一覧の1件目を取り、
 *   その画像を使う (以前からの経路)。縮小画像が取れなかったときも先頭ページに戻る。
 * @why: 取り込んだ資料には書誌の表紙データが無い。先頭ページ (表紙・扉) で代用すると、
 *   棚で背表紙のように見分けられる (代表、2026-08-24)。ただし先頭ページの経路は、表紙のためだけに
 *   本1冊ぶんのページ一覧 (数百 doc の復号) と原寸の画像 (300〜600KB) を資料ごとに取るので、
 *   本だなを開くたびに ISBN の無い本の数だけ重かった。縮小画像なら一覧の応答だけで場所が決まり、
 *   1枚20KB ほどで済む。一覧の JSON は資料ごとに1回だけ引き、棚の再描画やタブの行き来で取り直さない。
 *   表示中は acquire で参照を立てる。棚は資料の数だけ表紙を同時に出すので、
 *   参照なしの先読み (loadPageImage) で取ると資料が LRU の上限 (16 枚) を超えた時点で
 *   古い表紙の object URL が revoke され、一部のサムネイルだけ消える (代表 2026-08-29 報告。
 *   資料が 15 件から 20 件に増えて表面化した)。
 *   ISBN の書影で表紙が出せる資料 (components/SourceCover.tsx) では enabled=false で呼び、
 *   何も引かない (書影が引けなかったときだけ true に切り替えて取りに行く)。
 * @failure: ページ無し・取得失敗は "none" (カードは頭文字の枠を出す)。失敗はキャッシュに
 *   残さないので、開き直せば取り直す。acquire した path は unmount と資料の切り替えで release する。
 *   enabled=false の間は "loading" のまま (呼び出し側は書影を出しているので見た目に出ない)。
 */

import { useEffect, useState } from "react";
import { fetchSourcePages, type StudyPage } from "../api/pages";
import { acquirePageImage, releasePageImage } from "../lib/pageImageCache";

/** familyId/sourceId → 先頭ページ。取得中の Promise も共有する。 */
const firstPageCache = new Map<string, Promise<StudyPage | null>>();

export type SourceCoverState =
  | { status: "loading" }
  | { status: "none" }
  | { status: "ready"; url: string };

/** 表紙を引くのに必要な資料の項目 (api/sources.ts の StudySource がそのまま渡せる)。 */
export interface SourceCoverTarget {
  id: string;
  cover_path?: string;
  cover_content_type?: string;
}

export function useSourceCover(
  familyId: string,
  source: SourceCoverTarget,
  enabled: boolean = true,
): SourceCoverState {
  const [state, setState] = useState<SourceCoverState>({ status: "loading" });
  const sourceId = source.id;
  const coverPath = source.cover_path;
  const coverType = source.cover_content_type;

  useEffect(() => {
    let cancelled = false;
    // acquire できた path だけを覚える (throw した時に release で他の参照を減らさないため)。
    let acquiredPath: string | null = null;
    setState({ status: "loading" });
    if (!enabled) return;
    const acquire = (path: string, type: string): Promise<string> => {
      const promise = acquirePageImage(path, type);
      acquiredPath = path;
      return promise;
    };
    const fromFirstPage = async (): Promise<string | null> => {
      const key = `${familyId}/${sourceId}`;
      let first = firstPageCache.get(key);
      if (!first) {
        first = fetchSourcePages(familyId, sourceId).then((pages) => pages[0] ?? null);
        first.catch(() => firstPageCache.delete(key));
        firstPageCache.set(key, first);
      }
      const page = await first;
      if (!page || cancelled) return null;
      return acquire(page.image_path, page.content_type);
    };
    const run = async (): Promise<string | null> => {
      if (coverPath) {
        try {
          return await acquire(coverPath, coverType ?? "image/jpeg");
        } catch (err: unknown) {
          // 縮小画像が取れないときは先頭ページに戻る (以前と同じ見え方になる)。
          if (cancelled) return null;
          console.warn("[study] cover thumbnail unavailable, falling back to the first page", err);
        }
      }
      return fromFirstPage();
    };
    run()
      .then((url) => {
        if (!cancelled) setState(url ? { status: "ready", url } : { status: "none" });
      })
      .catch(() => {
        if (!cancelled) setState({ status: "none" });
      });
    return () => {
      cancelled = true;
      if (acquiredPath) releasePageImage(acquiredPath);
    };
  }, [familyId, sourceId, coverPath, coverType, enabled]);

  return state;
}

/** テスト用。先頭ページの覚えを空にする。 */
export function resetSourceCoverCache(): void {
  firstPageCache.clear();
}
